import { forwardRef, ForwardRefRenderFunction, useCallback, useState, useEffect, useMemo, useRef } from "react";
//#region redux
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { displayInfoMessage } from "store/actions/utilActions";
//#endregion
//#region types
import { SalaryDeviationFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { WintDeviationFormContainerProps } from "models/propTypes/wintSalaryPropTypes";
import {
  WintSuperkollAPIModelWintSalaryDeviations,
  WintSuperkollAPIModelWintSalaryWithdrawal,
} from "wint-api-client/dist/types";
//#endregion
//#region components
import { useWintSalaryDeviationService } from "serviceHooks";
import WintDeviationCreateForm from "./wintDeviationCreateForm";
import useSalaryDeviationValues from "../useSalaryDeviationValues";
import useDeviationPermissions from "components/salaryDeviation/usePermissions";
import { LoadingWithLogo } from "components/common";
import useWintDeviationValidation from "./useWintDeviationValidation";
import SalaryDeviationHelper from "./salaryDeviationHelper";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import { TranslationKeys } from "translations";
import cssExports from "theme/styles/main.module.scss";

const numericEmployeeId = (id: string | number | undefined): number | undefined =>
  id === undefined || id === "" ? undefined : Number(id);

const WintDeviationCreateFormContainer: ForwardRefRenderFunction<FormHandles, WintDeviationFormContainerProps> = (
  props,
  ref
) => {
  const { deviationId, employee, readOnly, lockedPeriod, filterPeriod, onFormUpdate, onInitialValuesLoaded } = props;
  const { canManageSalary } = useDeviationPermissions();
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const {
    createDeviationReapprove,
    updateDeviationReapprove,
    getPartialDeviationById,
    getAllDeviationTypes,
    convertApiResponseToForm,
    getDailyHoursTemplate,
    mergeDayHoursEdits,
    filterDayHoursByMonth,
    getChildrenForPersonApi,
    getWithdrawalBalancesApi,
  } = useWintSalaryDeviationService();

  const allDayHoursRef = useRef<SalaryDeviationFormTypes.HoursByDayWithMeta[]>([]);

  const [deviationTypes, setDeviationTypes] = useState<AutocompleteListItem[]>([]);
  const [children, setChildren] = useState<AutocompleteListItem[]>([]);
  const [withdrawalClassification, setWithdrawalClassification] = useState<
    WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeClassification | undefined
  >(undefined);
  const [withdrawalBalances, setWithdrawalBalances] = useState<
    WintSuperkollAPIModelWintSalaryWithdrawal.WithdrawalBalances | undefined
  >(undefined);
  const [withdrawalBalancesLoading, setWithdrawalBalancesLoading] = useState(false);
  const [withdrawalBalancesFailed, setWithdrawalBalancesFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [typesLoading, setTypesLoading] = useState(false);
  const [showInfoMessage, setShowInfoMessage] = useState(false);

  const { initialCreateValues, availableDeviationCategories, availableDeviationReportTypes } = useSalaryDeviationValues(
    deviationId ? undefined : employee,
    deviationTypes
  );

  const [initialValues, setInitialValues] = useState<SalaryDeviationFormTypes.WintDeviationCreate>(initialCreateValues);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | number | undefined>(
    initialCreateValues.employee?.id
  );

  // Cap by classification: the combined type is bounded by both pots at once (min(saved/(1−p),
  // vacation/p), computed server-side), the single-pot types by their own withdrawable. Undefined
  // in edit mode: the balance may already net out the deviation being edited, so capping there
  // could block an unchanged re-save.
  const withdrawalCap = useMemo(() => {
    if (deviationId || !withdrawalBalances || withdrawalClassification === undefined) return undefined;

    const Classification = WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeClassification;
    switch (withdrawalClassification) {
      case Classification.SavedSalaryAndVacationPayWithdrawal:
        return withdrawalBalances.combinedWithdrawalCap;
      case Classification.SavedSalaryWithdrawal:
        return withdrawalBalances.savedSalary?.withdrawable;
      case Classification.SalaryVacationPayWithdrawal:
        return withdrawalBalances.vacationPay?.withdrawable;
      default:
        return undefined;
    }
  }, [deviationId, withdrawalBalances, withdrawalClassification]);

  const { validationSchema } = useWintDeviationValidation(withdrawalCap);

  useEffect(() => {
    setSelectedEmployeeId(initialValues.employee?.id);
  }, [initialValues.employee?.id]);

  const handleFormUpdate = useCallback(
    (values: SalaryDeviationFormTypes.WintDeviationCreate, isValid: boolean) => {
      setSelectedEmployeeId(values.employee?.id);
      // Same predicate the section renders on, so the balance read never runs for a type the form
      // would not show one for.
      setWithdrawalClassification(
        SalaryDeviationHelper.shouldShowWithdrawalSection(values.deviationType, values.deviation)
          ? SalaryDeviationHelper.getWithdrawalClassification(values.deviation)
          : undefined
      );
      onFormUpdate?.(values, isValid);
    },
    [onFormUpdate]
  );

  useEffect(() => {
    if (!selectedEmployeeId) {
      setChildren([]);
      return;
    }
    getChildrenForPersonApi(Number(selectedEmployeeId))
      .then((result) => {
        setChildren(
          (result ?? []).map((child: WintSuperkollAPIModelWintSalaryDeviations.Child) => ({
            id: child.id,
            value: child.name ?? "",
          }))
        );
      })
      .catch(() => setChildren([]));
  }, [selectedEmployeeId]);

  // Balances are per employee and only meaningful for a withdrawal type, so the fetch is keyed on
  // both. A failure is a rendered state, not a thrown one — the section says so inline and the
  // amount field simply advertises no cap.
  useEffect(() => {
    if (!canManageSalary || !selectedEmployeeId || withdrawalClassification === undefined) {
      setWithdrawalBalances(undefined);
      setWithdrawalBalancesFailed(false);
      // Loading is cleared here too: an in-flight fetch cancelled by this same change has its
      // finally suppressed by the cancelled guard, so this branch is the only thing left to
      // reset the flag. Without it the panel renders blank forever on the next selection.
      setWithdrawalBalancesLoading(false);
      return;
    }

    let cancelled = false;
    // Cleared before the read, not after it: the cap derives from these balances, so holding the
    // previous employee's (or classification's) values would let the amount field advertise and
    // validate against a cap that no longer applies while the new read is in flight.
    setWithdrawalBalances(undefined);
    setWithdrawalBalancesLoading(true);
    setWithdrawalBalancesFailed(false);
    getWithdrawalBalancesApi(Number(selectedEmployeeId))
      .then((balances) => {
        if (cancelled) return;
        setWithdrawalBalances(balances);
      })
      .catch(() => {
        if (cancelled) return;
        setWithdrawalBalances(undefined);
        setWithdrawalBalancesFailed(true);
      })
      .finally(() => {
        if (!cancelled) setWithdrawalBalancesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedEmployeeId, withdrawalClassification, canManageSalary]);

  // Create mode owns the employee-scoped type fetch (mount + on employee change).
  // The cancelled guard ignores a stale response if the employee changes mid-flight.
  // Edit mode keeps the full list resolved by loadExistingData (employee is fixed).
  useEffect(() => {
    if (deviationId) return;
    let cancelled = false;
    setTypesLoading(true);
    getAllDeviationTypes(numericEmployeeId(selectedEmployeeId))
      .then((types) => {
        if (!cancelled) setDeviationTypes(types);
      })
      .catch(() => {
        if (!cancelled) setDeviationTypes([]);
      })
      .finally(() => {
        if (!cancelled) setTypesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedEmployeeId, deviationId]);

  const handleChildCreated = useCallback(
    (child: WintSuperkollAPIModelWintSalaryDeviations.Child): AutocompleteListItem => {
      const item: AutocompleteListItem = {
        id: child.id,
        value: child.name ?? "",
      };
      setChildren((prev) => [...prev, item]);
      return item;
    },
    []
  );

  useEffect(() => {
    // Create mode does not fetch types here — the employee-keyed effect owns that
    // fetch so a stale mount-time response can't overwrite the employee-scoped list.
    if (!deviationId) return;

    const loadExistingData = async () => {
      try {
        setLoading(true);

        // Convert filterPeriod dates to yearMonth numbers if provided
        let startYearMonth: number | undefined;
        let endYearMonth: number | undefined;

        if (filterPeriod?.start && filterPeriod?.end) {
          startYearMonth = DateHelper.getYearMonth(filterPeriod.start);
          endYearMonth = DateHelper.getYearMonth(filterPeriod.end);
        }

        const [deviationData, deviationTypes] = await Promise.all([
          getPartialDeviationById(deviationId, startYearMonth, endYearMonth),
          getAllDeviationTypes(),
        ]);

        setDeviationTypes(deviationTypes);

        if (deviationData) {
          let template: WintSuperkollAPIModelWintSalaryDeviations.DayMinutesTemplate[] | undefined;
          const deviationType = deviationTypes.find((type) => type.id === deviationData.deviationTypeId);
          const deviationTypeData = deviationType?.data;
          const isAbsence =
            deviationTypeData?.category ===
            WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeCategory.Absence;
          // Vacation deviations are always full days - no per-day hours table, so no template needed
          const isVacation = SalaryDeviationHelper.isVacationClassification(deviationType);

          if (isAbsence && !isVacation && deviationData.dayMinutes?.length && deviationData.reportedPersonId) {
            try {
              template = await getDailyHoursTemplate({
                personId: deviationData.reportedPersonId,
                startDate: deviationData.fromDate,
                endDate: deviationData.toDate,
              });
            } catch (templateError) {
              console.error("Failed to fetch template, using defaults:", templateError);
            }
          }
          const formData = convertApiResponseToForm(deviationData, deviationTypes, template, canManageSalary);
          allDayHoursRef.current = formData.timeDeviation?.dayHours ?? [];
          if (formData.timeDeviation?.selectedPeriod && formData.timeDeviation.dayHours?.length) {
            formData.timeDeviation = {
              ...formData.timeDeviation,
              dayHours: filterDayHoursByMonth(formData.timeDeviation.dayHours, formData.timeDeviation.selectedPeriod),
            };
          }
          if (deviationData.periodYearMonthStates?.length) {
            const hasApprovedInRange = deviationData.periodYearMonthStates.some(
              (p) =>
                (p.state === WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationState.Approved ||
                  p.state === WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationState.SoftApproved) &&
                (!startYearMonth || p.yearMonth >= startYearMonth) &&
                (!endYearMonth || p.yearMonth <= endYearMonth)
            );
            setShowInfoMessage(hasApprovedInRange);
          }
          setInitialValues(formData);
          onInitialValuesLoaded?.(formData);
        }
      } catch (error) {
        console.error("Failed to load deviation data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadExistingData();
  }, [deviationId, filterPeriod]);

  const handleSubmit = async (values: SalaryDeviationFormTypes.WintDeviationCreate) => {
    let submitValues = values;

    if (deviationId && allDayHoursRef.current.length > 0 && values.timeDeviation) {
      const merged = mergeDayHoursEdits(allDayHoursRef.current, values.timeDeviation.dayHours ?? []);
      submitValues = {
        ...values,
        timeDeviation: { ...values.timeDeviation, dayHours: merged },
      };
    }

    if (deviationId) {
      // Build the full yearMonth range considering both timeDeviation dateRange and filterPeriod
      const startDates = [submitValues.timeDeviation?.dateRange?.start, filterPeriod?.start].filter(Boolean) as Date[];
      const endDates = [submitValues.timeDeviation?.dateRange?.end, filterPeriod?.end].filter(Boolean) as Date[];

      let yearMonths: number[] | undefined;
      if (startDates.length > 0 && endDates.length > 0) {
        const rangeStart = new Date(Math.min(...startDates.map((d) => d.getTime())));
        const rangeEnd = new Date(Math.max(...endDates.map((d) => d.getTime())));
        yearMonths = DateHelper.getYearMonthRange(rangeStart, rangeEnd);
      }

      // Filter out months the user cannot edit based on permissions
      const filteredYearMonths = yearMonths?.filter((ym) => {
        const state = submitValues.timeDeviation?.yearMonthStates?.find((s) => s.yearMonth === ym);
        if (!state) return true;

        // Finalized months are never editable
        if (state.isYearMonthFinalized) return false;

        // With canManageSalary: can edit any non-finalized state
        if (canManageSalary) return true;

        // Without canManageSalary: only unhandled states are editable
        return state.state === WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationState.Unhandled;
      });

      if (filteredYearMonths && filteredYearMonths.length === 0) return;

      const yearMonthFrom = filteredYearMonths?.length ? Math.min(...filteredYearMonths) : undefined;
      const yearMonthTo = filteredYearMonths?.length ? Math.max(...filteredYearMonths) : undefined;
      const deviationResult = await updateDeviationReapprove(deviationId, submitValues, yearMonthFrom, yearMonthTo);
      return deviationResult.data;
    } else {
      const deviationResult = await createDeviationReapprove(submitValues);

      // Mirrors convertCreateFormToApiPost's clamp: comparing the raw form value would report an
      // override the server never made, and re-trust the state the clamp exists to distrust.
      const requestedPlacement =
        (submitValues.useCurrentMonthPlacement ?? false) &&
        SalaryDeviationHelper.allowsCurrentMonthPlacementChoice(submitValues.deviation);

      // Boolean, not `!== undefined`: this is `bool?` on a base shared by six operations and
      // Superkoll sends nulls, which pass an undefined check and then read as arrears.
      const storedPlacement = deviationResult.data?.useCurrentMonthPlacement;
      if (typeof storedPlacement === "boolean" && storedPlacement !== requestedPlacement) {
        dispatch(
          displayInfoMessage(
            LocalizationHelperInstance.getFormattedMessage(
              storedPlacement
                ? TranslationKeys.SalaryDeviation.PlacementInheritedToCurrentMonth
                : TranslationKeys.SalaryDeviation.PlacementInheritedToArrears
            ),
            // Global, like every other salary dispatch: the speed dial opens this dialog on /Todo, where
            // reduxMessage hands a non-global message to a todo card's inner provider instead of showing
            // it where the modal just closed.
            { isGlobal: true }
          )
        );
      }

      return deviationResult.data;
    }
  };

  return (
    <>
      {loading && (
        <div className={cssExports.dialogLoadingContainer}>
          <LoadingWithLogo />
        </div>
      )}
      <WintDeviationCreateForm
        ref={ref}
        availableDeviationTypes={availableDeviationCategories}
        availableDeviationReportTypes={availableDeviationReportTypes}
        defaultValues={initialValues}
        handleSubmit={handleSubmit}
        validationSchema={validationSchema}
        allDeviationTypes={deviationTypes}
        typesLoading={typesLoading}
        editMode={!!deviationId}
        readOnly={readOnly}
        lockedPeriod={lockedPeriod}
        allDayHoursRef={deviationId ? allDayHoursRef : undefined}
        onFormUpdate={handleFormUpdate}
        showInfoMessage={showInfoMessage}
        availableChildren={children}
        onChildCreated={handleChildCreated}
        withdrawalBalances={withdrawalBalances}
        withdrawalBalancesLoading={withdrawalBalancesLoading}
        withdrawalBalancesFailed={withdrawalBalancesFailed}
        withdrawalCap={withdrawalCap}
      />
    </>
  );
};

export default forwardRef(WintDeviationCreateFormContainer);
